import './movement.scss';

import {
    faAngleDown,
    faAngleRight,
    faCircleNotch,
    faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';
import {
    Item,
    ItemDataStore,
    itemSelectors,
    itemSorters,
    Movement,
    MovementDataStore,
    MovementLifecycleStates,
    movementSelectors,
    Task,
    TaskDataStore,
    taskSelectors,
    ThesaurusTerm,
    ThesaurusTermDataStore,
    thesaurusTermSelectors,
} from '@skinsoft/entity';
import { LinkableItemLifecycleStates } from '@skinsoft/entity-data-store';
import { MovementFeaturedCard, MovementItemCard } from '@skinsoft/entity-ui';
import { ReactiveVue } from '@skinsoft/reactive-vue';
import { AddItemModal, Featured, ItemCard, PullToRight } from '@skinsoft/ui';
import { combineLatest } from 'rxjs';
import { Component, Watch } from 'vue-property-decorator';
import { Route } from 'vue-router';

import {
    ADD_ITEM_TOPIC,
    CANCEL_MOVEMENT_TOPIC,
    CLOSE_MOVEMENT_TOPIC,
    RETURN_MOVEMENT_TOPIC,
} from '../../../busTopics';
import { router } from '../../../router';
import { CloseMovementModal } from '../../component/close-movement-modal';

@Component({
    components: {
        sAddItemModal: AddItemModal,
        sCloseMovementModal: CloseMovementModal,
        sItemCard: ItemCard,
        sMovementFeaturedCard: MovementFeaturedCard,
        sMovementItemCard: MovementItemCard,
        sPullToRight: PullToRight,
    },
    template: require('./movement.html'),
})
export class MovementPage extends ReactiveVue {
    public $movementDataStore: MovementDataStore;
    public angleDownIcon = faAngleDown;
    public angleRightIcon = faAngleRight;
    public spinnerIcon = faCircleNotch;
    public movement: Movement = null as any;
    public motive: ThesaurusTerm = null as any;
    public associatedTask: Task = null as any;
    public $route: Route;
    public $itemDataStore: ItemDataStore;
    public $taskDataStore: TaskDataStore;
    public $thesaurusTermDataStore: ThesaurusTermDataStore;
    public $refs: {
        fakeEntity: HTMLDivElement;
        item: ItemCard[];
        addItemModal: AddItemModal;
        closeMovementModal: CloseMovementModal;
        puller: PullToRight;
        viewport: HTMLDivElement;
    };

    public movementCreationPending = false;
    public showCollapse = false;
    public addItemModalMounted: boolean = false;
    public exclamationTriangleIcon = faExclamationTriangle;
    public closeMovementModalMounted: boolean = false;
    public featured: Featured = null as any;
    public focusedItemId: Item['id'] = null as any;
    public items: Item[] = [null, null, null] as any[];

    public committedItems: string[] = [];

    get title() {
        return movementSelectors.title(this.movement);
    }

    get associatedTaskIncrementalTitle() {
        return taskSelectors.incrementalTitle(this.associatedTask);
    }

    public mounted() {
        this.subscribeToBus(ADD_ITEM_TOPIC, this.addItem.bind(this));
        this.subscribeToBus(CANCEL_MOVEMENT_TOPIC, this.cancelMovement.bind(this));
        this.subscribeToBus(CLOSE_MOVEMENT_TOPIC, this.closeMovement.bind(this));
        this.subscribeToBus(RETURN_MOVEMENT_TOPIC, this.createReturnMovement.bind(this));

        this.subscribeToMovement();
    }

    public subscribeToMovement() {
        this.subscribeTo(
            this.$movementDataStore.fetchOne(this.$route.params.id),
            movement => {
                this.movement = movement;
            },
            'movement',
        );
    }

    public subscribeToMotive() {
        const motiveId = movementSelectors.motive(this.movement);

        if (!motiveId) {
            return;
        }

        this.subscribeTo(
            this.$thesaurusTermDataStore.fetchOne(motiveId),
            thesaurusTerm => {
                this.motive = thesaurusTermSelectors.preferredTerm(thesaurusTerm);
            },
            'motive',
        );
    }

    public subscribeToAssociatedTask() {
        const associatedTaskId = movementSelectors.associatedTasks(this.movement)[0];

        if (!associatedTaskId) {
            return;
        }

        this.subscribeTo(
            this.$taskDataStore.fetchOne(associatedTaskId),
            associatedTask => {
                this.associatedTask = associatedTask;
            },
            'associatedTask',
        );
    }

    public subscribeToItems() {
        const linkableItems = movementSelectors.linkableItems(this.movement);
        const state = movementSelectors.state(this.movement);

        linkableItems.filter(linkableItem => {
            switch (state) {
                default:
                    return true;

                case MovementLifecycleStates.FINALIZED:
                case MovementLifecycleStates.ARCHIVED:
                case MovementLifecycleStates.CANCELED:
                    return (
                        linkableItem.state &&
                        linkableItem.state.name === LinkableItemLifecycleStates.FINALIZED
                    );
            }
        });

        if (!linkableItems || linkableItems.length === 0) {
            this.items = [];
            return;
        }

        this.subscribeTo(
            combineLatest(
                ...linkableItems.map(item => this.$itemDataStore.fetchOne(item.linkableItem)),
            ),
            items => {
                this.items = items.sort(itemSorters.byRealTimeLocation) as Item[];
            },
            'items',
        );
    }

    @Watch('$route', { deep: true })
    public onRouteChange() {
        // to hide the reset of the puller, we reset the state of this page to make it look like the same as the mockup view
        this.items = [null, null, null] as any[];
        this.$refs.puller.reset();

        this.refreshMovement()

        this.focusedItemId = null as any;
    }

    @Watch('movement')
    public onEntityChange() {
        this.unsubscribeFrom('motive');
        this.unsubscribeFrom('associatedTask');
        this.unsubscribeFrom('items');
        this.subscribeToAssociatedTask();
        this.subscribeToMotive();
        this.subscribeToItems();
    }

    public onPulled() {
        // the user has reach pull-right non return point, we must transition to the next entity
        const next = this.$route.query.next as any;
        const id = (next as string[]).shift() as string;

        requestAnimationFrame(() =>
            router.push({
                name: this.$route.name || "",
                params: { id },
                query: (next.length > 0 ? { next } : {}) as Record<string, string>,
            }),
        );
    }

    public focusItem(item: Item) {
        const id = itemSelectors.id(item);

        if (this.committedItems.indexOf(id) !== -1) {
            return; // we can't focus already validated items
        }

        this.focusedItemId = id;
    }

    public rollbackItem(item: Item) {
        if (!this.isItemCommitted(item)) {
            return;
        }

        const id = itemSelectors.id(item);
        const index = this.committedItems.indexOf(id);
        this.committedItems.splice(index, 1);
    }

    public commitItem(item: Item) {
        const id = itemSelectors.id(item);

        if (this.focusedItemId === id) {
            this.focusedItemId = null as any; // we can't focus already validated items
        }

        if (this.isItemCommitted(item)) {
            return;
        }

        this.committedItems.push(id);
    }

    public hasItemFocus(item: Item) {
        return this.focusedItemId === itemSelectors.id(item);
    }

    public isItemCommitted(item: Item) {
        return this.committedItems.indexOf(itemSelectors.id(item)) !== -1;
    }

    public toggleCollapse() {
        this.showCollapse = !this.showCollapse;
    }

    public goToAssociatedTask(event: MouseEvent) {
        event.preventDefault();
        event.stopPropagation();

        if (!this.associatedTask) {
            return;
        }
        this.$router.push(`/tasks/${this.associatedTask.id}`);
    }

    /**
     * Add item to movement's linkableItems and refresh movement data
     * @param item 
     */
    private async onAddedItemSelected(item: Item) {
        try {
            await this.$movementDataStore.addItem(this.movement.id, item.id, item.type);
            this.unsubscribeFrom('movement')
            this.subscribeToMovement()
        } catch (error) {
            console.error(error)
        }
    }

    private get linkableItems() {
        if (this.movement) {
            return movementSelectors.linkableItems(this.movement)
                .map(item => item.linkableItem)
        }
    }


    private get itemsIdsToExclude() {
        const linkableItems = movementSelectors.linkableItems(this.movement);
        return linkableItems || [];
    }

    private async createReturnMovement() {
        this.waitForUserApproval(this.$root.$t('entity.createReturnMovement') as string);

        this.movementCreationPending = true;

        let movement: Movement;
        try {
            movement = await (this.$movementDataStore as MovementDataStore).createReturnMovement(
                this.movement.id,
            );
        } finally {
            this.movementCreationPending = false;
        }

        if (movement) {
            router.push({
                name: this.$route.name || "",
                params: { id: movement.id },
            });
        }
    }

    private addItem() {
        this.focusedItemId = null as any; // to prevent from filling the input if scan mode is enable
        this.$refs.addItemModal.open();
    }

    private closeMovement() {
        this.focusedItemId = null as any; // to prevent from filling the input if scan mode is enable
        if (this.$refs.closeMovementModal) {
            this.$refs.closeMovementModal.open();
        }
    }

    private cancelMovement() {
        this.waitForUserApproval(this.$t('entity.cancelMovement') as string);

        return (this.$movementDataStore as MovementDataStore).cancelMovement(this.movement.id);
    }

    private refreshMovement() {
        this.unsubscribeFrom('movement');
        this.subscribeToMovement();
    }

}
